import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Chess } from 'chess.js';
import { 
  Play, Pause, SkipBack, SkipForward, 
  ChevronLeft, ChevronRight, Home, 
  Loader2, AlertCircle, Crown, Skull, Handshake,
  Eye, Clock, User, Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { getSharedReplay } from '@/utils/replayService';
import { getCoachSuggestion, analyzePosition } from '@/engine/stockfishAI';

// Simple chess piece component for replay
const ReplayPiece = ({ piece }) => {
  const pieceSymbols = {
    'wk': '♔', 'wq': '♕', 'wr': '♖', 'wb': '♗', 'wn': '♘', 'wp': '♙',
    'bk': '♚', 'bq': '♛', 'br': '♜', 'bb': '♝', 'bn': '♞', 'bp': '♟'
  };
  
  const key = `${piece.color}${piece.type}`;
  
  return (
    <span className={`text-4xl select-none ${piece.color === 'w' ? 'text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]' : 'text-gray-900'}`}>
      {pieceSymbols[key] || '?'}
    </span>
  );
};

// Mini board component for replay
const ReplayBoard = ({ fen, lastMove, playerColor }) => {
  const game = useMemo(() => new Chess(fen), [fen]);
  const board = game.board();
  
  const ranks = playerColor === 'white' ? [0,1,2,3,4,5,6,7] : [7,6,5,4,3,2,1,0];
  const files = playerColor === 'white' ? [0,1,2,3,4,5,6,7] : [7,6,5,4,3,2,1,0];
  
  return (
    <div className="grid grid-cols-8 gap-0 rounded-lg overflow-hidden shadow-2xl border-2 border-purple-500/30">
      {ranks.map(r => (
        files.map(f => {
          const piece = board[r][f];
          const isLight = (r + f) % 2 === 0;
          const square = String.fromCharCode(97 + f) + (8 - r);
          const isLastMove = lastMove && (lastMove.from === square || lastMove.to === square);
          
          return (
            <div
              key={`${r}-${f}`}
              className={`
                aspect-square flex items-center justify-center
                ${isLight 
                  ? 'bg-gradient-to-br from-indigo-900/80 to-purple-900/80' 
                  : 'bg-gradient-to-br from-slate-900 to-indigo-950'
                }
                ${isLastMove ? 'bg-yellow-500/30' : ''}
              `}
            >
              {piece && <ReplayPiece piece={piece} />}
            </div>
          );
        })
      ))}
    </div>
  );
};

const ReplayPage = () => {
  const { replayId } = useParams();
  const navigate = useNavigate();
  
  const [replay, setReplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Playback state
  const [currentMoveIndex, setCurrentMoveIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1000); // ms per move
  
  // Analysis state
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Load replay
  useEffect(() => {
    const loadReplay = async () => {
      try {
        setLoading(true);
        const data = await getSharedReplay(replayId);
        setReplay(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadReplay();
  }, [replayId]);

  // Calculate current position based on move index
  const currentPosition = useMemo(() => {
    if (!replay) return 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    
    const game = new Chess();
    for (let i = 0; i <= currentMoveIndex && i < replay.history.length; i++) {
      try {
        game.move(replay.history[i]);
      } catch (e) {
        console.error('Invalid move:', replay.history[i]);
      }
    }
    return game.fen();
  }, [replay, currentMoveIndex]);

  // Get last move for highlighting
  const lastMove = useMemo(() => {
    if (!replay || currentMoveIndex < 0) return null;
    
    const game = new Chess();
    for (let i = 0; i <= currentMoveIndex && i < replay.history.length; i++) {
      try {
        const move = game.move(replay.history[i]);
        if (i === currentMoveIndex) {
          return { from: move.from, to: move.to };
        }
      } catch (e) {
        return null;
      }
    }
    return null;
  }, [replay, currentMoveIndex]);

  // Playback effect
  useEffect(() => {
    if (!isPlaying || !replay) return;
    
    const timer = setInterval(() => {
      setCurrentMoveIndex(prev => {
        if (prev >= replay.history.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, playbackSpeed);
    
    return () => clearInterval(timer);
  }, [isPlaying, playbackSpeed, replay]);

  // Analyze current position
  const analyzeCurrentPosition = useCallback(async () => {
    if (!showAnalysis || isAnalyzing) return;
    
    setIsAnalyzing(true);
    try {
      const game = new Chess(currentPosition);
      const [suggestion, analysis] = await Promise.all([
        getCoachSuggestion(game),
        analyzePosition(currentPosition)
      ]);
      
      setCurrentAnalysis({
        suggestion,
        evaluation: analysis
      });
    } catch (e) {
      console.error('Analysis error:', e);
    } finally {
      setIsAnalyzing(false);
    }
  }, [currentPosition, showAnalysis, isAnalyzing]);

  useEffect(() => {
    if (showAnalysis) {
      analyzeCurrentPosition();
    }
  }, [currentPosition, showAnalysis]);

  // Navigation handlers
  const goToStart = () => { setCurrentMoveIndex(-1); setIsPlaying(false); };
  const goToEnd = () => { setCurrentMoveIndex(replay.history.length - 1); setIsPlaying(false); };
  const goToPrev = () => { setCurrentMoveIndex(prev => Math.max(-1, prev - 1)); setIsPlaying(false); };
  const goToNext = () => { setCurrentMoveIndex(prev => Math.min(replay.history.length - 1, prev + 1)); setIsPlaying(false); };
  const togglePlay = () => setIsPlaying(!isPlaying);

  const getResultIcon = () => {
    if (!replay?.result) return null;
    if (replay.result === 'win') return <Crown className="w-6 h-6 text-yellow-400" />;
    if (replay.result === 'loss') return <Skull className="w-6 h-6 text-red-400" />;
    return <Handshake className="w-6 h-6 text-gray-400" />;
  };

  const getResultText = () => {
    if (!replay?.result) return 'In Progress';
    if (replay.result === 'win') return 'Victory';
    if (replay.result === 'loss') return 'Defeat';
    return 'Draw';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading replay...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Replay Not Found</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-cyan-600 hover:bg-cyan-700">
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-2 sm:p-4 md:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Back to Game</span>
            <span className="sm:hidden">Back</span>
          </Button>
          
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {replay.views} views
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Board Section */}
        <div className="lg:col-span-2 space-y-3 sm:space-y-4">
          {/* Game Title */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/80 rounded-xl p-3 sm:p-4 border border-purple-500/30"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white">{replay.game_name}</h1>
                <p className="text-gray-400 text-xs sm:text-sm mt-1">
                  Shared by {replay.shared_by} • {new Date(replay.shared_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {getResultIcon()}
                <span className={`
                  text-base sm:text-lg font-semibold
                  ${replay.result === 'win' ? 'text-yellow-400' : 
                    replay.result === 'loss' ? 'text-red-400' : 'text-gray-400'}
                `}>
                  {getResultText()}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Chess Board */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="aspect-square w-full max-w-[min(100vw-1rem,560px)] mx-auto"
          >
            <ReplayBoard 
              fen={currentPosition} 
              lastMove={lastMove}
              playerColor={replay.player_color}
            />
          </motion.div>

          {/* Playback Controls */}
          <div className="bg-slate-900/80 rounded-xl p-3 sm:p-4 border border-purple-500/30">
            <div className="flex items-center justify-center gap-1 sm:gap-2 mb-3 sm:mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={goToStart}
                disabled={currentMoveIndex < 0}
                className="text-gray-400 hover:text-white disabled:opacity-30 w-8 h-8 sm:w-10 sm:h-10"
              >
                <SkipBack className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPrev}
                disabled={currentMoveIndex < 0}
                className="text-gray-400 hover:text-white disabled:opacity-30 w-8 h-8 sm:w-10 sm:h-10"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </Button>
              <Button
                onClick={togglePlay}
                className={`
                  w-12 h-12 sm:w-14 sm:h-14 rounded-full
                  ${isPlaying 
                    ? 'bg-orange-600 hover:bg-orange-700' 
                    : 'bg-cyan-600 hover:bg-cyan-700'
                  }
                `}
                data-testid="play-pause-btn"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                  <Play className="w-5 h-5 sm:w-6 sm:h-6 ml-0.5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNext}
                disabled={currentMoveIndex >= replay.history.length - 1}
                className="text-gray-400 hover:text-white disabled:opacity-30"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={goToEnd}
                disabled={currentMoveIndex >= replay.history.length - 1}
                className="text-gray-400 hover:text-white disabled:opacity-30"
              >
                <SkipForward className="w-5 h-5" />
              </Button>
            </div>

            {/* Progress Slider */}
            <div className="px-2">
              <Slider
                value={[currentMoveIndex + 1]}
                onValueChange={(value) => { setCurrentMoveIndex(value[0] - 1); setIsPlaying(false); }}
                min={0}
                max={replay.history.length}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Start</span>
                <span>Move {currentMoveIndex + 1} / {replay.history.length}</span>
                <span>End</span>
              </div>
            </div>

            {/* Speed Control */}
            <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-slate-700">
              <span className="text-gray-400 text-sm">Speed:</span>
              {[2000, 1000, 500, 250].map(speed => (
                <Button
                  key={speed}
                  variant={playbackSpeed === speed ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPlaybackSpeed(speed)}
                  className={playbackSpeed === speed ? 'bg-purple-600' : 'text-gray-400'}
                >
                  {speed === 2000 ? '0.5x' : speed === 1000 ? '1x' : speed === 500 ? '2x' : '4x'}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Game Info */}
          <div className="bg-slate-900/80 rounded-xl p-4 border border-purple-500/30">
            <h3 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Game Info
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Played as</span>
                <span className="text-white capitalize">{replay.player_color}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Difficulty</span>
                <span className="text-white capitalize">{replay.difficulty}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Total Moves</span>
                <span className="text-white">{replay.history.length}</span>
              </div>
            </div>
          </div>

          {/* Move History */}
          <div className="bg-slate-900/80 rounded-xl p-4 border border-purple-500/30">
            <h3 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Move History
            </h3>
            <div className="max-h-48 overflow-y-auto">
              <div className="grid grid-cols-2 gap-1 text-sm font-mono">
                {replay.history.map((move, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setCurrentMoveIndex(idx); setIsPlaying(false); }}
                    className={`
                      px-2 py-1 rounded text-left transition-colors
                      ${idx === currentMoveIndex 
                        ? 'bg-cyan-600 text-white' 
                        : 'text-gray-400 hover:bg-slate-700'
                      }
                    `}
                  >
                    {idx % 2 === 0 && <span className="text-gray-500">{Math.floor(idx/2)+1}.</span>} {move}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Coach Analysis */}
          <div className="bg-slate-900/80 rounded-xl p-4 border border-purple-500/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-cyan-400 font-semibold flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Coach Analysis
              </h3>
              <Button
                variant={showAnalysis ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowAnalysis(!showAnalysis)}
                className={showAnalysis ? 'bg-cyan-600' : 'border-purple-500/30 text-purple-300'}
              >
                {showAnalysis ? 'On' : 'Off'}
              </Button>
            </div>
            
            {showAnalysis && (
              <div className="space-y-3">
                {isAnalyzing ? (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </div>
                ) : currentAnalysis?.suggestion ? (
                  <>
                    <div className="p-3 rounded-lg bg-cyan-900/30 border border-cyan-500/30">
                      <p className="text-xs text-cyan-400 uppercase tracking-wider mb-1">Best Move</p>
                      <p className="text-white font-semibold">
                        {currentAnalysis.suggestion.san || `${currentAnalysis.suggestion.from} → ${currentAnalysis.suggestion.to}`}
                      </p>
                      {currentAnalysis.suggestion.explanation && (
                        <p className="text-gray-400 text-sm mt-1">
                          {currentAnalysis.suggestion.explanation}
                        </p>
                      )}
                    </div>
                    {currentAnalysis.evaluation && (
                      <div className="text-sm">
                        <span className="text-gray-400">Evaluation: </span>
                        <span className={`font-mono ${
                          currentAnalysis.evaluation.evalPercent > 55 ? 'text-green-400' :
                          currentAnalysis.evaluation.evalPercent < 45 ? 'text-red-400' : 'text-gray-300'
                        }`}>
                          {currentAnalysis.evaluation.evalPercent > 50 ? '+' : ''}
                          {((currentAnalysis.evaluation.evalPercent - 50) / 10).toFixed(1)}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">No analysis available</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplayPage;
